const TAG_ID = 'AW-18470181502';
type GoogleWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
let loaded = false;
const reported = new Set<string>();
let generation = 0;
let running: Promise<void> | null = null;
export const googleAdsLoaded = () => loaded;
export function stopGoogleAds() {
  generation++;
  if (loaded) (window as GoogleWindow).gtag?.('consent', 'update', {
    ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'denied',
  });
}
/** The server exposes only canonical new-account events; Google deduplicates their transaction IDs. */
export function startGoogleAds(hasOptedOut: () => boolean): Promise<void> {
  if (running) return running;
  const current = generation;
  const allowed = () => {
    try {
      return import.meta.env.MODE === 'production' && typeof window !== 'undefined' && typeof document !== 'undefined' && typeof navigator !== 'undefined'
        && window.location.origin === 'https://userill.dev' && current === generation && !hasOptedOut()
        && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl
        && !/(?:^|;\s*)rill_analytics_enabled=0(?:;|$)/.test(document.cookie);
    } catch { return false; }
  };
  running = (async () => {
    if (!allowed()) return;
    try {
      const response = await fetch('/api/advertising/google', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(3000) });
      if (!response.ok || !allowed()) return;
      const config = await response.json() as { enabled?: boolean; conversion?: { eventId: string; value: number; currency: string } | null };
      if (!config.enabled || !allowed()) return;
      const target = window as GoogleWindow;
      if (!loaded) {
        target.dataLayer ||= [];
        target.gtag ||= function () { target.dataLayer!.push(arguments); };
        target.gtag('consent', 'default', {
          ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'denied', analytics_storage: 'denied',
        });
        target.gtag('js', new Date());
        const pageLocation = new URL('https://userill.dev/');
        let originalSearch = window.location.search;
        try {
          const navigation = performance.getEntriesByType('navigation')[0];
          if (navigation) originalSearch = new URL(navigation.name).search;
        } catch { /* Navigation timing is optional. */ }
        const params = new URLSearchParams(originalSearch);
        for (const key of ['gclid', 'gbraid', 'wbraid']) {
          const value = params.get(key);
          if (value && /^[A-Za-z0-9_.~-]{1,512}$/.test(value)) pageLocation.searchParams.set(key, value);
        }
        // Only Google's attribution IDs accompany the public root, never private page metadata.
        target.gtag('config', TAG_ID, {
          page_location: pageLocation.href, page_referrer: '', page_title: 'Rill',
          allow_google_signals: false, allow_ad_personalization_signals: false,
          allow_enhanced_conversions: false,
        });
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${TAG_ID}`;
        script.referrerPolicy = 'no-referrer';
        loaded = true;
        document.head.appendChild(script);
      }
      const event = config.conversion;
      if (!event || !allowed() || reported.has(event.eventId)) return;
      const key = `rill_ad_google_${event.eventId}`;
      try { if (sessionStorage.getItem(key)) return; } catch { /* Google also deduplicates by transaction_id. */ }
      target.gtag?.('event', 'conversion', {
        send_to: 'AW-18470181502/gsffCK-YxoIdEP60oudE', value: event.value, currency: event.currency,
        transaction_id: event.eventId,
        event_callback: () => {
          // SDK processing is not a delivery receipt. Failed/blocked loads can retry on a later page.
          try { sessionStorage.setItem(key, '1'); } catch { /* Google deduplicates repeated IDs. */ }
        },
      });
      reported.add(event.eventId);

    } catch { /* Advertising availability must not block the app. */ }
  })().finally(() => { running = null; });
  return running;
}
